import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import { CopyValuesButton } from 'elements/react-admin/CopyValuesButton'
import TinyBox from 'elements/TinyBox'
import { TinyLabel } from 'elements/TinyLabel'
import Tooltip from 'elements/tooltip/Tooltip'
import lodash from 'lodash'
import { Button, Dialog, DialogCloseButton, DialogContent, DialogTitle, EyeIcon, Typography } from 'opensolar-ui'
import { useState } from 'react'
import { useForm } from 'react-final-form'
import { doNotTranslate } from 'util/misc'
import { StructVersionBrief } from './types'

const useStyles = makeStyles(
  (theme) => ({
    versions: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      overflowY: 'auto',

      [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
    },
    buttons: { paddingTop: 8, width: '100%', display: 'flex', gap: 8 },
    comment: {
      whiteSpace: 'pre-wrap',
      background: theme.palette.grey[200],
      maxWidth: 300,
      width: '100%',
      padding: 8,
      boxSizing: 'border-box',
      borderRadius: 4,
      fontFamily: 'monospace',
    },
  }),
  { name: 'StructVersionList' }
)

type Props = {
  isAdStructType: boolean
  versions?: StructVersionBrief[]
  is_published: boolean
  is_completed: boolean
  is_archived: boolean
}

export const StructVersionList = ({ isAdStructType, versions, is_published, is_completed, is_archived }: Props) => {
  const classes = useStyles()
  const form = useForm()
  const [previewing, setPreviewing] = useState<undefined | StructVersionBrief>()

  const restore = (v: StructVersionBrief) => {
    form.change('data', v.fields.data)
  }

  const preview = (v: StructVersionBrief) => {
    setPreviewing(v)
  }

  const combineVersions = (versionsDESC: StructVersionBrief[]) => {
    // the version tracker makes multiple records when multiple tracked fields get updated at once
    // here we recombine them into single records for easier readability
    let ret: StructVersionBrief[] = []
    let lastVersion: StructVersionBrief | undefined
    let lastDate: dayjs.Dayjs

    versionsDESC.forEach((version) => {
      if (
        !lastVersion ||
        lastVersion.author?.email !== version.author?.email ||
        !lastDate ||
        lastDate.diff(version.created_at, 'seconds', true) < -3 // 3s collation
      ) {
        lastVersion = lodash.cloneDeep(version)
        lastDate = dayjs(version.created_at)
        ret.push(lastVersion)
        return
      }
      // combine
      for (let field in version.fields) {
        lastVersion.fields[field] = version.fields[field]
      }
    })

    return ret
  }

  const generateList = (
    versions: StructVersionBrief[],
    is_published: boolean,
    is_completed: boolean,
    is_archived: boolean
  ) => {
    let versionsASC = versions.concat().reverse()
    let elements: JSX.Element[] = []
    let is_published_now = is_published
    let is_completed_now = is_completed
    let is_archived_now = is_archived
    versionsASC.forEach((v) => {
      const otherFields = Object.keys(v.fields).filter((f) => f !== 'is_published' && f !== 'is_archived')
      const date = dayjs(v.created_at)
      elements.push(
        <div style={{ opacity: (is_completed_now || is_published_now) && !is_archived_now ? 1 : 0.75 }}>
          <TinyBox key={v.hash}>
            <TinyLabel label={doNotTranslate('Version:')}>
              {v.hash} (
              <Tooltip title={date.format('LLL')}>
                <strong>{date.format('DD/MM/YYYY')}</strong>
              </Tooltip>
              )
            </TinyLabel>
            <TinyLabel label={doNotTranslate('Author:')}>{v.author?.email || 'Auto-created'}</TinyLabel>
            {v.fields.is_published !== undefined && (
              <TinyLabel label={doNotTranslate('Published:')}>{v.fields.is_published ? 'Yes' : 'No'}</TinyLabel>
            )}
            {isAdStructType && v.fields.is_completed !== undefined && (
              <TinyLabel label={doNotTranslate('Completed:')}>{v.fields.is_completed ? 'Yes' : 'No'}</TinyLabel>
            )}
            {v.fields.is_archived !== undefined && (
              <TinyLabel label={doNotTranslate('Archived:')}>{v.fields.is_archived ? 'Yes' : 'No'}</TinyLabel>
            )}
            {otherFields.length > 0 && (
              <div className={classes.buttons}>
                <Tooltip title={doNotTranslate('Copy Data')}>
                  <CopyValuesButton size="small" label="" values={v.fields} resource="" />
                </Tooltip>
                <Button onClick={() => preview(v)} variant="contained" size="small" color="default">
                  <EyeIcon />
                  {doNotTranslate('View')}
                </Button>
                <Button onClick={() => restore(v)} variant="outlined" size="small" color="primary">
                  {doNotTranslate('Restore')}
                </Button>
              </div>
            )}
          </TinyBox>
        </div>
      )

      // This is a little confusing, but because we're going 'back in time', we
      // set the value to the inverse of the change seen here (hence !)
      if (v.fields.is_published !== undefined) {
        is_published_now = !v.fields.is_published
      }
      if (v.fields.is_archived !== undefined) {
        is_archived_now = !v.fields.is_archived
      }
    })

    return elements.reverse()
  }

  if (!versions?.length) return <></>
  return (
    <div>
      <Typography variant="h6">{doNotTranslate('History')}</Typography>
      <div className={classes.versions}>
        {generateList(combineVersions(versions), is_published, is_completed, is_archived)}
      </div>
      <Dialog open={!!previewing} onClose={() => setPreviewing(undefined)}>
        <DialogTitle>
          <span>{doNotTranslate('Previewing: ' + previewing?.hash)}</span>
          <DialogCloseButton onClick={() => setPreviewing(undefined)} />
        </DialogTitle>
        <DialogContent>
          <div className="code-block">{JSON.stringify(previewing?.fields, null, ' ')}</div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
